import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const DataProcessingHistory = () => {
  const [selectedDateRange, setSelectedDateRange] = useState('7days');
  const [selectedProcessingType, setSelectedProcessingType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const dateRangeOptions = [
    { value: '24hours', label: 'Last 24 Hours' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const processingTypeOptions = [
    { value: 'all', label: 'All Processing Types' },
    { value: 'data-scraping', label: 'Data Scraping' },
    { value: 'sentiment-analysis', label: 'Sentiment Analysis' },
    { value: 'trend-analysis', label: 'Trend Analysis' },
    { value: 'report-generation', label: 'Report Generation' },
    { value: 'data-export', label: 'Data Export' }
  ];

  const processingHistory = [
    {
      id: 1,
      type: "sentiment-analysis",
      title: "Consumer Sentiment Analysis - Q4 2024",
      status: "completed",
      startTime: "2024-10-29T09:15:00Z",
      endTime: "2024-10-29T09:47:00Z",
      duration: "32m 15s",
      dataSource: "Ipsos Global Survey",
      recordsProcessed: 15847,
      outputSize: "2.3 GB",
      user: "sarah.wilson@ipsos.com",
      accuracy: 94.2,
      insights: 287
    },
    {
      id: 2,
      type: "data-scraping",
      title: "ONS Population Data Collection",
      status: "running",
      startTime: "2024-10-29T14:20:00Z",
      endTime: null,
      duration: "Running for 2h 15m",
      dataSource: "ONS Public API",
      recordsProcessed: 8934,
      outputSize: "1.1 GB",
      user: "mike.chen@ipsos.com",
      accuracy: null,
      insights: 0
    },
    {
      id: 3,
      type: "trend-analysis",
      title: "Market Trend Identification - Tech Sector",
      status: "failed",
      startTime: "2024-10-29T11:30:00Z",
      endTime: "2024-10-29T11:45:00Z",
      duration: "15m 23s",
      dataSource: "UNData Economic Indicators",
      recordsProcessed: 2456,
      outputSize: "0.4 GB",
      user: "emma.taylor@ipsos.com",
      accuracy: null,
      insights: 0
    },
    {
      id: 4,
      type: "report-generation",
      title: "Monthly Brand Performance Report",
      status: "completed",
      startTime: "2024-10-28T16:45:00Z",
      endTime: "2024-10-28T17:12:00Z",
      duration: "27m 8s",
      dataSource: "Integrated Survey Data",
      recordsProcessed: 12389,
      outputSize: "5.7 GB",
      user: "john.doe@ipsos.com",
      accuracy: 97.8,
      insights: 423
    },
    {
      id: 5,
      type: "data-export",
      title: "Power BI Dashboard Data Export",
      status: "completed",
      startTime: "2024-10-28T14:20:00Z",
      endTime: "2024-10-28T14:25:00Z",
      duration: "5m 12s",
      dataSource: "Dashboard Analytics",
      recordsProcessed: 3456,
      outputSize: "0.8 GB",
      user: "alex.rodriguez@ipsos.com",
      accuracy: 100,
      insights: 89
    }
  ];

  const filteredHistory = processingHistory?.filter(item => {
    const typeMatch = selectedProcessingType === 'all' || item?.type === selectedProcessingType;
    const searchMatch = searchQuery === '' || 
      item?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.dataSource?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return typeMatch && searchMatch;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'running': return 'Clock';
      case 'failed': return 'XCircle';
      case 'paused': return 'Pause';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'running': return 'text-warning';
      case 'failed': return 'text-destructive';
      case 'paused': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'data-scraping': return 'Database';
      case 'sentiment-analysis': return 'Heart';
      case 'trend-analysis': return 'TrendingUp';
      case 'report-generation': return 'FileText';
      case 'data-export': return 'Download';
      default: return 'Activity';
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Data Processing History</h3>
          <p className="text-sm text-text-secondary mt-1">
            Audit trail of all data processing operations and analytics
          </p>
        </div>
        <Button variant="outline" iconName="Filter">
          Advanced Filters
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label="Date Range"
          options={dateRangeOptions}
          value={selectedDateRange}
          onChange={setSelectedDateRange}
        />
        <Select
          label="Processing Type"
          options={processingTypeOptions}
          value={selectedProcessingType}
          onChange={setSelectedProcessingType}
        />
        <Input
          label="Search"
          type="search"
          placeholder="Search by title or data source..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
        />
      </div>
      <div className="space-y-4">
        {filteredHistory?.length === 0 ? (
          <div className="text-center py-12 bg-muted rounded-lg">
            <Icon name="Database" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Processing History</h4>
            <p className="text-text-secondary">
              No data processing records match the selected criteria
            </p>
          </div>
        ) : (
          filteredHistory?.map((item) => (
            <div key={item?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon 
                      name={getTypeIcon(item?.type)} 
                      size={20} 
                      className="text-primary"
                    />
                    <Icon 
                      name={getStatusIcon(item?.status)} 
                      size={16} 
                      className={getStatusColor(item?.status)}
                    />
                    <h4 className="text-md font-semibold text-card-foreground">{item?.title}</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-text-secondary">Processing Type:</span>
                      <p className="text-card-foreground font-medium capitalize">
                        {item?.type?.replace('-', ' ')}
                      </p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Duration:</span>
                      <p className="text-card-foreground font-medium">{item?.duration}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Records Processed:</span>
                      <p className="text-card-foreground font-medium">
                        {item?.recordsProcessed?.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Output Size:</span>
                      <p className="text-card-foreground font-medium">{item?.outputSize}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-text-secondary">Data Source:</span>
                      <p className="text-card-foreground font-medium">{item?.dataSource}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Started:</span>
                      <p className="text-card-foreground font-medium">
                        {new Date(item.startTime)?.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-text-secondary">User:</span>
                      <p className="text-card-foreground font-medium">{item?.user}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Insights:</span>
                      <p className="text-card-foreground font-medium">{item?.insights}</p>
                    </div>
                  </div>
                  
                  {item?.accuracy && (
                    <div className="mt-4 flex items-center gap-2">
                      <span className="text-text-secondary text-sm">Accuracy:</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-success transition-all duration-300"
                            style={{ width: `${item?.accuracy}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-success">{item?.accuracy}%</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    <Icon name="Eye" size={16} />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Icon name="Download" size={16} />
                  </Button>
                  {item?.status === 'running' && (
                    <Button variant="destructive" size="sm">
                      <Icon name="Square" size={16} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DataProcessingHistory;